import { type FormEvent, type ReactNode, useEffect, useState } from 'react';

import {
  type AdKit,
  buildKit,
  fetchKitVersions,
  KIT_FONT_ROLES,
  KIT_HEADLINE_CASES,
  KIT_PALETTE_ROLES,
  KIT_TEXT_ROLES,
  type KitBandStyle,
  type KitPaletteRole,
  type KitPreview,
  type KitSource,
  type KitVersion,
  previewKit,
  restoreKitVersion,
  saveKit,
} from './api.ts';
import { describeError, redirectIfUnauthorised, useAppData } from './data.tsx';
import { Icon } from './Icon.tsx';
import { Link } from './router.tsx';
import { localTime } from './schedule.ts';
import { useToast } from './toast.tsx';
import { Badge, Notice, Panel, Skeleton } from './ui.tsx';

/**
 * The ad kit (one-shot ads US-007): the brand's ad look, drawn as it will be
 * in an ad — a reel frame with the band, a headline and the caption band, a
 * 4:5 frame and the end card — from the same `kit.css` and component CSS a
 * workspace links, so what is seen here is what compose gets.
 *
 * Every field is edited in place and the frames follow the draft before it is
 * saved; saving makes a new version, and any earlier version can be restored
 * as the next one. Nothing here asks to be approved: a kit is applied the
 * moment it is saved, and undone by restoring.
 */

/** The frames are drawn at the preset's real size and shown at this scale. */
const FRAME_SCALE = 0.16;
const FRAMES = [
  { key: 'reel', label: 'Reel 9:16', width: 1080, height: 1920 },
  { key: 'portrait', label: 'Feed 4:5', width: 1080, height: 1350 },
  { key: 'endCard', label: 'End card', width: 1080, height: 1920 },
] as const;

const SOURCE_LABEL: Record<KitSource, string> = {
  built: 'built',
  edited: 'edited',
  learned: 'learned',
};

const WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];
const BAND_LABELS = { captionBand: 'Caption band', band: 'Band', lowerThird: 'Lower third' } as const;
const TEXT_LABELS = { headline: 'Headline', tag: 'Tag', body: 'Body', caption: 'Captions' } as const;

/** How long the draft rests before the frames are drawn again. */
const PREVIEW_DELAY_MS = 350;

export function AdKitPanel({ brandBookId, name }: { readonly brandBookId: string; readonly name: string }) {
  const toast = useToast();
  const { contents } = useAppData();
  const [versions, setVersions] = useState<KitVersion[] | null>(null);
  const [draft, setDraft] = useState<AdKit | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'build' | `restore-${number}` | null>(null);
  const [preview, setPreview] = useState<KitPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const current = versions?.[0] ?? null;

  const load = (list: KitVersion[]): void => {
    setVersions(list);
    setDraft(list[0] === undefined ? null : structuredClone(list[0].kit));
    setNote('');
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchKitVersions(brandBookId, controller.signal)
      .then((list) => {
        load(list);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
      });
    return () => controller.abort();
  }, [brandBookId]);

  // The frames follow the draft: an invalid draft keeps the last good
  // drawing and says which field the server refused.
  const draftJson = draft === null ? null : JSON.stringify(draft);
  useEffect(() => {
    if (draftJson === null) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      previewKit(brandBookId, JSON.parse(draftJson) as AdKit, controller.signal)
        .then((drawn) => {
          setPreview(drawn);
          setPreviewError(null);
        })
        .catch((cause: unknown) => {
          if (controller.signal.aborted || redirectIfUnauthorised(cause)) return;
          setPreviewError(describeError(cause));
        });
    }, PREVIEW_DELAY_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [brandBookId, draftJson]);

  const dirty = current !== null && draftJson !== JSON.stringify(current.kit);

  const update = (change: (kit: AdKit) => void): void => {
    setDraft((before) => {
      if (before === null) return before;
      const next = structuredClone(before);
      change(next);
      return next;
    });
  };

  const refresh = async (): Promise<void> => {
    load(await fetchKitVersions(brandBookId));
  };

  const onSave = (event: FormEvent): void => {
    event.preventDefault();
    if (draft === null || !dirty) return;
    setBusy('save');
    saveKit(brandBookId, draft, note.trim() === '' ? null : note.trim())
      .then(async (saved) => {
        toast.ok(`Saved version ${String(saved.version)} of the ad kit of ${name}.`);
        await refresh();
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  };

  const onRestore = (version: number): void => {
    setBusy(`restore-${version}`);
    restoreKitVersion(brandBookId, version)
      .then(async (saved) => {
        toast.ok(`Restored version ${String(version)} as version ${String(saved.version)}.`);
        await refresh();
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  };

  const onBuild = (): void => {
    setBusy('build');
    buildKit(brandBookId)
      .then(async (result) => {
        if (result.ok) toast.ok(result.message);
        else toast.error(result.message);
        await refresh();
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  };

  const contentName = (id: string): string =>
    contents?.find((content) => content.id === id)?.name ?? id;

  return (
    <Panel
      title="Ad kit"
      icon="paintbrush"
      id={`ad-kit-${brandBookId}`}
      meta={
        current === null ? (
          versions === null ? null : <Badge tone="neutral">none yet</Badge>
        ) : (
          <Badge tone="done">{`version ${String(current.version)}`}</Badge>
        )
      }
    >
      {error !== null && <Notice kind="error">{error}</Notice>}
      {versions === null ? (
        error === null && <Skeleton lines={4} />
      ) : current === null || draft === null ? (
        <div className="stack">
          <p className="field__hint">
            {name} has no ad kit yet. One is built from the brand profile, the product profile and
            the house rules when the first content is made in this book, or now.
          </p>
          <div className="field__actions">
            <button
              type="button"
              className="button button--small"
              disabled={busy !== null}
              onClick={onBuild}
            >
              <Icon name={busy === 'build' ? 'sync' : 'zap'} />
              {busy === 'build' ? 'Building the ad kit…' : 'Build the ad kit'}
            </button>
          </div>
        </div>
      ) : (
        <div className="stack">
          <p className="field__hint">
            The look every ad of {name} uses: compose links it and does not design one of its own.
            The frames follow your edits; saving makes a new version, which applies to every content
            still being planned.
          </p>

          <KitFrames preview={preview} />
          {previewError !== null && <Notice kind="error">Cannot draw this kit: {previewError}</Notice>}
          {preview !== null && preview.missingFiles.length > 0 && (
            <Notice kind="warn">
              The brand book has no file {preview.missingFiles.map((file) => `“${file}”`).join(', ')}; ads
              show it as missing.
            </Notice>
          )}

          <form className="form form--tight" onSubmit={onSave}>
            <KitEditor brandBookId={brandBookId} kit={draft} update={update} />
            <div className="field">
              <label className="field__label" htmlFor={`kit-note-${brandBookId}`}>
                What changed
              </label>
              <input
                id={`kit-note-${brandBookId}`}
                className="field__input"
                value={note}
                maxLength={1000}
                placeholder="Optional, shown in the version list"
                onChange={(event) => {
                  setNote(event.target.value);
                }}
              />
            </div>
            <div className="field__actions">
              <button
                type="submit"
                className="button button--primary button--small"
                disabled={!dirty || busy !== null}
              >
                <Icon name={busy === 'save' ? 'sync' : 'check'} />
                {busy === 'save' ? 'Saving…' : `Save as version ${String(current.version + 1)}`}
              </button>
              <button
                type="button"
                className="button button--small button--quiet"
                disabled={!dirty || busy !== null}
                onClick={() => {
                  setDraft(structuredClone(current.kit));
                }}
              >
                Discard changes
              </button>
            </div>
          </form>

          <KitVersionList
            versions={versions}
            busy={busy}
            contentName={contentName}
            onRestore={onRestore}
          />
        </div>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------ frames */

function KitFrames({ preview }: { readonly preview: KitPreview | null }) {
  return (
    <div className="kit__frames">
      {FRAMES.map((frame) => (
        <figure className="kit__frame" key={frame.key}>
          <div
            className="kit__viewport"
            style={{ width: frame.width * FRAME_SCALE, height: frame.height * FRAME_SCALE }}
          >
            {preview === null ? (
              <Skeleton lines={3} />
            ) : (
              // No `allow-*` flag: the frame runs no script and has an
              // opaque origin, so a kit's text can never reach the page.
              <iframe
                title={`${frame.label} with the ad kit`}
                sandbox=""
                srcDoc={preview[frame.key]}
                width={frame.width}
                height={frame.height}
                style={{ transform: `scale(${String(FRAME_SCALE)})` }}
              />
            )}
          </div>
          <figcaption className="kit__caption">{frame.label}</figcaption>
        </figure>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ editor */

function KitEditor({
  brandBookId,
  kit,
  update,
}: {
  readonly brandBookId: string;
  readonly kit: AdKit;
  readonly update: (change: (kit: AdKit) => void) => void;
}) {
  const id = (field: string): string => `kit-${field}-${brandBookId}`;

  return (
    <>
      <KitSection title="Palette">
        <div className="field__row">
          {KIT_PALETTE_ROLES.map((role) => (
            <div className="field" key={role}>
              <label className="field__label" htmlFor={id(`palette-${role}`)}>
                {role}
              </label>
              <div className="field__pair">
                <input
                  type="color"
                  className="kit__swatch"
                  aria-label={`${role} colour picker`}
                  value={/^#[0-9a-f]{6}$/i.test(kit.palette[role]) ? kit.palette[role] : '#000000'}
                  onChange={(event) => {
                    update((next) => {
                      next.palette[role] = event.target.value;
                    });
                  }}
                />
                <input
                  id={id(`palette-${role}`)}
                  className="field__input mono"
                  value={kit.palette[role]}
                  onChange={(event) => {
                    update((next) => {
                      next.palette[role] = event.target.value;
                    });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="field">
          <span className="field__label">May fill a whole frame</span>
          <div className="kit__checks">
            {KIT_PALETTE_ROLES.map((role) => (
              <label className="checkbox" key={role}>
                <input
                  type="checkbox"
                  checked={kit.palette.fieldRoles.includes(role)}
                  onChange={(event) => {
                    update((next) => {
                      const others = next.palette.fieldRoles.filter((one) => one !== role);
                      next.palette.fieldRoles = event.target.checked
                        ? KIT_PALETTE_ROLES.filter((one) => one === role || others.includes(one))
                        : others;
                    });
                  }}
                />
                {role}
              </label>
            ))}
          </div>
          <p className="field__hint">Every field colour needs a lockup below.</p>
        </div>
        <div className="field__row">
          {KIT_PALETTE_ROLES.map((role) => (
            <TextInput
              key={role}
              id={id(`meaning-${role}`)}
              label={`${role} means`}
              value={kit.palette.roles?.[role] ?? ''}
              placeholder={role === 'accent' ? 'decision' : 'nothing of its own'}
              onChange={(value) => {
                update((next) => {
                  const roles = { ...next.palette.roles };
                  const word = value.trim().toLowerCase();
                  if (word === '') Reflect.deleteProperty(roles, role);
                  else roles[role] = word;
                  if (Object.keys(roles).length === 0) Reflect.deleteProperty(next.palette, 'roles');
                  else next.palette.roles = roles;
                });
              }}
            />
          ))}
        </div>
        <p className="field__hint">
          A colour with a meaning (the accent as `decision` when the brand guide says so) is only for what it means:
          never a band, and in an ad only on elements marked `data-mc-palette-role`.
        </p>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={kit.glow === true}
            onChange={(event) => {
              update((next) => {
                if (event.target.checked) next.glow = true;
                else Reflect.deleteProperty(next, 'glow');
              });
            }}
          />
          A drifting glow on the world layer (otherwise grain, a texture or a blurred plate of the place)
        </label>
      </KitSection>

      <KitSection title="Fonts">
        {KIT_FONT_ROLES.map((role) => (
          <div className="field__row" key={role}>
            <TextInput
              id={id(`font-${role}`)}
              label={`${role} family`}
              value={kit.fonts[role].family}
              onChange={(value) => {
                update((next) => {
                  next.fonts[role].family = value;
                });
              }}
            />
            <TextInput
              id={id(`font-${role}-source`)}
              label={`${role} source`}
              value={kit.fonts[role].source ?? ''}
              placeholder="https://… or fonts/name.woff2; empty for a system font"
              onChange={(value) => {
                update((next) => {
                  next.fonts[role].source = value.trim() === '' ? null : value;
                });
              }}
            />
          </div>
        ))}
      </KitSection>

      <KitSection title="Type">
        <div className="field__row">
          {KIT_TEXT_ROLES.map((role) => (
            <div className="field" key={role}>
              <span className="field__label">{TEXT_LABELS[role]}</span>
              <div className="field__pair">
                <select
                  className="field__input"
                  aria-label={`${TEXT_LABELS[role]} font`}
                  value={kit.typography[role].font}
                  onChange={(event) => {
                    update((next) => {
                      next.typography[role].font = event.target.value as AdKit['typography']['headline']['font'];
                    });
                  }}
                >
                  {KIT_FONT_ROLES.map((font) => (
                    <option key={font} value={font}>
                      {font}
                    </option>
                  ))}
                </select>
                <WeightSelect
                  label={`${TEXT_LABELS[role]} weight`}
                  value={kit.typography[role].weight}
                  onChange={(weight) => {
                    update((next) => {
                      next.typography[role].weight = weight;
                    });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor={id('headline-case')}>
              Headline case
            </label>
            <select
              id={id('headline-case')}
              className="field__input"
              value={kit.typography.headlineCase}
              onChange={(event) => {
                update((next) => {
                  next.typography.headlineCase = event.target.value as AdKit['typography']['headlineCase'];
                });
              }}
            >
              {KIT_HEADLINE_CASES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <TextInput
            id={id('headline-tracking')}
            label="Headline tracking"
            value={kit.typography.headlineTracking}
            placeholder="-0.02em"
            onChange={(value) => {
              update((next) => {
                next.typography.headlineTracking = value;
              });
            }}
          />
        </div>
      </KitSection>

      <KitSection title="Logos">
        <div className="field__row">
          {KIT_PALETTE_ROLES.map((role) => (
            <TextInput
              key={role}
              id={id(`lockup-${role}`)}
              label={`Lockup on ${role}`}
              value={kit.logos.lockup[role] ?? ''}
              placeholder={kit.palette.fieldRoles.includes(role) ? 'logos/lockup.svg' : 'not a field colour'}
              onChange={(value) => {
                update((next) => {
                  if (value.trim() === '') delete next.logos.lockup[role];
                  else next.logos.lockup[role] = value;
                });
              }}
            />
          ))}
          <TextInput
            id={id('mark')}
            label="Mark"
            value={kit.logos.mark}
            onChange={(value) => {
              update((next) => {
                next.logos.mark = value;
              });
            }}
          />
        </div>
        <p className="field__hint">
          Paths in the brand book’s kit store. Logos appear on the end card only.
        </p>
      </KitSection>

      <KitSection title="End card">
        <div className="field__row">
          <RoleSelect
            id={id('end-field')}
            label="Field"
            value={kit.endCard.field}
            onChange={(role) => {
              update((next) => {
                next.endCard.field = role;
                const lockup = next.logos.lockup[role];
                if (lockup !== undefined) next.endCard.lockup = lockup;
              });
            }}
          />
          <TextInput
            id={id('end-lockup')}
            label="Lockup"
            value={kit.endCard.lockup}
            onChange={(value) => {
              update((next) => {
                next.endCard.lockup = value;
              });
            }}
          />
          <TextInput
            id={id('end-ask')}
            label="Ask"
            value={kit.endCard.ask}
            onChange={(value) => {
              update((next) => {
                next.endCard.ask = value;
              });
            }}
          />
          <TextInput
            id={id('end-url')}
            label="Address"
            value={kit.endCard.url}
            onChange={(value) => {
              update((next) => {
                next.endCard.url = value;
              });
            }}
          />
        </div>
        <div className="field__row">
          <RoleSelect
            id={id('button-fill')}
            label="Button fill"
            value={kit.endCard.button.fill}
            onChange={(role) => {
              update((next) => {
                next.endCard.button.fill = role;
              });
            }}
          />
          <RoleSelect
            id={id('button-text')}
            label="Button text"
            value={kit.endCard.button.text}
            onChange={(role) => {
              update((next) => {
                next.endCard.button.text = role;
              });
            }}
          />
          <TextInput
            id={id('button-radius')}
            label="Button radius"
            value={kit.endCard.button.radius}
            onChange={(value) => {
              update((next) => {
                next.endCard.button.radius = value;
              });
            }}
          />
          <div className="field">
            <span className="field__label">Button weight</span>
            <WeightSelect
              label="Button weight"
              value={kit.endCard.button.weight}
              onChange={(weight) => {
                update((next) => {
                  next.endCard.button.weight = weight;
                });
              }}
            />
          </div>
        </div>
        <p className="field__hint">
          The lockup, the ask, the address on the button — and nothing else, no price or offer.
        </p>
      </KitSection>

      <KitSection title="Bands">
        {(['captionBand', 'band', 'lowerThird'] as const).map((band) => (
          <BandFields
            key={band}
            id={id(band)}
            label={BAND_LABELS[band]}
            style={kit[band]}
            onChange={(change) => {
              update((next) => {
                change(next[band]);
              });
            }}
          />
        ))}
      </KitSection>
    </>
  );
}

function KitSection({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <fieldset className="kit__section">
      <legend className="brand__label">{title}</legend>
      {children}
    </fieldset>
  );
}

function TextInput({
  id,
  label,
  value,
  placeholder,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly placeholder?: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="field__input"
        value={value}
        {...(placeholder === undefined ? {} : { placeholder })}
        spellCheck={false}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </div>
  );
}

function RoleSelect({
  id,
  label,
  value,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: KitPaletteRole;
  readonly onChange: (role: KitPaletteRole) => void;
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="field__input"
        value={value}
        onChange={(event) => {
          onChange(event.target.value as KitPaletteRole);
        }}
      >
        {KIT_PALETTE_ROLES.map((role) => (
          <option key={role} value={role}>
            {role}
          </option>
        ))}
      </select>
    </div>
  );
}

function WeightSelect({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: number;
  readonly onChange: (weight: number) => void;
}) {
  return (
    <select
      className="field__input field__input--narrow"
      aria-label={label}
      value={value}
      onChange={(event) => {
        onChange(Number(event.target.value));
      }}
    >
      {WEIGHTS.map((weight) => (
        <option key={weight} value={weight}>
          {weight}
        </option>
      ))}
    </select>
  );
}

function BandFields({
  id,
  label,
  style,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly style: KitBandStyle;
  readonly onChange: (change: (style: KitBandStyle) => void) => void;
}) {
  return (
    <div className="field__row">
      <div className="field">
        <label className="field__label" htmlFor={`${id}-fill`}>
          {label} fill
        </label>
        <select
          id={`${id}-fill`}
          className="field__input"
          value={style.fill}
          onChange={(event) => {
            onChange((next) => {
              next.fill = event.target.value as KitBandStyle['fill'];
            });
          }}
        >
          {[...KIT_PALETTE_ROLES, 'none' as const].map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
      </div>
      <RoleSelect
        id={`${id}-text`}
        label={`${label} text`}
        value={style.text}
        onChange={(role) => {
          onChange((next) => {
            next.text = role;
          });
        }}
      />
      <TextInput
        id={`${id}-radius`}
        label={`${label} radius`}
        value={style.radius}
        onChange={(value) => {
          onChange((next) => {
            next.radius = value;
          });
        }}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- versions */

function KitVersionList({
  versions,
  busy,
  contentName,
  onRestore,
}: {
  readonly versions: readonly KitVersion[];
  readonly busy: string | null;
  readonly contentName: (id: string) => string;
  readonly onRestore: (version: number) => void;
}) {
  return (
    <div className="field">
      <span className="field__label">Versions</span>
      <ul className="rows rows--divided">
        {versions.map((version, index) => (
          <li className="row" key={version.id}>
            <div className="row__main">
              <span className="row__title">
                Version {version.version}
                <Badge tone="neutral">{SOURCE_LABEL[version.source]}</Badge>
                {index === 0 && <Badge tone="done">current</Badge>}
              </span>
              <span className="row__meta">
                {localTime(version.createdAt)}
                {version.sourceContentId !== null && (
                  <>
                    {' · from '}
                    <Link className="link" href={`/content/${encodeURIComponent(version.sourceContentId)}`}>
                      {contentName(version.sourceContentId)}
                    </Link>
                  </>
                )}
                {version.note !== null && ` · ${version.note}`}
              </span>
            </div>
            {index > 0 && (
              <div className="row__actions">
                <button
                  type="button"
                  className="button button--small"
                  disabled={busy !== null}
                  onClick={() => {
                    onRestore(version.version);
                  }}
                >
                  <Icon name={busy === `restore-${version.version}` ? 'sync' : 'history'} />
                  Restore
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

